import { isCanlan } from '../utils/customer-name'

// Common types
interface Organization {
  id: string
  name: string
  name_full: string
  abbreviation: string
  email: string | null
  website: string | null
  phone: string | null
  support_email: string | null
  support_website: string | null
  support_phone: string | null
  is_public: boolean
  updated_at: string
  is_favorite: boolean
  sf_id: string | null
  can_contain_teams: boolean
  can_contain_organizations: boolean
  can_contain_schedules: boolean
  can_contain_locations: boolean
  can_contain_feeds: boolean
  sync_salesforce: boolean
  parent_id: string
  children_count: number
  teams_count: number
  schedules_count: number
  is_playoff: boolean
  show_waiver: boolean
  waiver_id: string | null
  organization_type_id: number
  settings: Record<string, unknown>
}

interface BasePermissionParams {
  adminPermission: boolean
  updatePermission: boolean
  rosterId: string
  sync_salesforce: boolean
  organization: Organization
  isCurrentDateAfterDivisionOrCompetitionLockDate?: boolean
}

interface ShouldAllowAddPlayerParams extends BasePermissionParams {
  canAddPlayersFromRoster: boolean
  isRegistrationOn: boolean
}

interface ShouldAllowAddImportRosterParams {
  adminPermission: boolean
  updatePermission: boolean
  rosterId: string
  sync_salesforce: boolean
  isCurrentDateAfterDivisionOrCompetitionLockDate?: boolean
  isRegistrationOn: boolean
  canAddPlayersFromRoster: boolean
}

interface ShouldAllowAddTeamOfficialsParams {
  adminPermission: boolean
  updatePermission: boolean
  rosterId: string
  sync_salesforce: boolean
  organization: Organization
}

/**
 * Helper function to check basic permissions for non-Canlan rosters
 */
const hasBasicNonCanlanPermissions = ({
  adminPermission,
  updatePermission,
  rosterId,
  organization,
  requireOrganization = true,
  isCurrentDateAfterDivisionOrCompetitionLockDate = false,
}: Pick<
  BasePermissionParams,
  | 'adminPermission'
  | 'updatePermission'
  | 'rosterId'
  | 'organization'
  | 'isCurrentDateAfterDivisionOrCompetitionLockDate'
> & {
  requireOrganization?: boolean
}) => {
  // Admin always has permission
  if (adminPermission) {
    return true
  }

  // Cannot perform action after lock date
  if (isCurrentDateAfterDivisionOrCompetitionLockDate) {
    return false
  }

  // Basic permission check
  const hasBasicPermissions = updatePermission && Boolean(rosterId)
  return requireOrganization
    ? hasBasicPermissions && Boolean(organization?.id)
    : hasBasicPermissions
}

/**
 * Helper function to check if Salesforce sync blocks permission
 */
const isSalesforceBlocking = (
  sync_salesforce: boolean,
  adminPermission: boolean
) => {
  return sync_salesforce && !adminPermission
}

/**
 * Determines if a user is allowed to add a player to a roster based on their permissions and organization type.
 *
 * For Non-Canlan Organizations:
 * - During registration: Requires update permission, roster access, valid roster ID and organization, before lock date
 * - Outside registration: Admin can always add, non-admin needs update permission, valid IDs, and must be before lock date
 *
 * For Canlan Organizations:
 * - During registration: Requires update permission, roster access, valid roster ID and organization
 * - Outside registration: Admin can always add, non-admin needs update permission and valid IDs
 * - Salesforce sync must be disabled for non-admin users
 *
 * @param {ShouldAllowAddPlayerParams} params - Permission check parameters
 * @param {boolean} params.adminPermission - Whether user has admin permission
 * @param {boolean} params.updatePermission - Whether user has update permission
 * @param {string} params.rosterId - The roster ID
 * @param {boolean} params.sync_salesforce - Whether Salesforce sync is enabled
 * @param {Organization} params.organization - The organization object
 * @param {boolean} params.isCurrentDateAfterDivisionOrCompetitionLockDate - Whether current date is after lock date
 * @param {boolean} params.canAddPlayersFromRoster - Whether user can add players from roster
 * @param {boolean} params.isRegistrationOn - Whether registration period is active
 * @returns {boolean} Whether user is allowed to add players
 */
export const shouldAllowAddPlayer = ({
  adminPermission,
  updatePermission,
  rosterId,
  sync_salesforce,
  organization,
  isCurrentDateAfterDivisionOrCompetitionLockDate = false,
  canAddPlayersFromRoster,
  isRegistrationOn,
}: ShouldAllowAddPlayerParams) => {
  // For non-Canlan rosters
  if (!isCanlan) {
    // Special rules during registration period
    if (isRegistrationOn) {
      // Cannot add players after lock date
      if (isCurrentDateAfterDivisionOrCompetitionLockDate) {
        return false
      }
      // Check if user can add players from roster
      return (
        canAddPlayersFromRoster &&
        updatePermission &&
        Boolean(rosterId) &&
        Boolean(organization?.id)
      )
    }

    // Use helper function for standard permission check
    return hasBasicNonCanlanPermissions({
      adminPermission,
      updatePermission,
      rosterId,
      organization,
      isCurrentDateAfterDivisionOrCompetitionLockDate,
    })
  }

  // For Canlan rosters

  // Salesforce sync restriction
  if (isSalesforceBlocking(sync_salesforce, adminPermission)) {
    return false
  }

  // Admin always has permission
  if (adminPermission) {
    return true
  }

  // Special rules during registration period
  if (isRegistrationOn) {
    return (
      canAddPlayersFromRoster &&
      updatePermission &&
      Boolean(rosterId) &&
      Boolean(organization?.id)
    )
  }

  // Default permission check
  return updatePermission && Boolean(rosterId) && Boolean(organization?.id)
}

/**
 * Determines if a user is allowed to import a roster based on their permissions and organization type.
 *
 * For Non-Canlan Organizations:
 * - Admin can always import before lock date
 * - Non-admin needs update permission, valid roster ID, and must be before lock date
 *
 * For Canlan Organizations:
 * - Admin can always import
 * - Non-admin needs update permission and valid roster ID
 * - Salesforce sync must be disabled for non-admin users
 *
 * @param {ShouldAllowAddImportRosterParams} params - Permission check parameters
 * @param {boolean} params.adminPermission - Whether user has admin permission
 * @param {boolean} params.updatePermission - Whether user has update permission
 * @param {string} params.rosterId - The roster ID
 * @param {boolean} params.sync_salesforce - Whether Salesforce sync is enabled
 * @param {boolean} params.isCurrentDateAfterDivisionOrCompetitionLockDate - Whether current date is after lock date
 * @param {boolean} params.isRegistrationOn - Whether registration period is active
 * @param {boolean} params.canAddPlayersFromRoster - Whether user can add players from roster
 * @returns {boolean} Whether user is allowed to import roster
 */
export const shouldAllowAddImportRoster = ({
  adminPermission,
  updatePermission,
  rosterId,
  sync_salesforce,
  isCurrentDateAfterDivisionOrCompetitionLockDate = false,
  isRegistrationOn = false,
  canAddPlayersFromRoster = true,
}: ShouldAllowAddImportRosterParams) => {
  // For non-Canlan rosters
  if (!isCanlan) {
    const hasBasicNonCanlanPermissionsResult = hasBasicNonCanlanPermissions({
      adminPermission,
      updatePermission,
      rosterId,
      organization: {} as Organization,
      requireOrganization: false,
      isCurrentDateAfterDivisionOrCompetitionLockDate,
    })
    if (isRegistrationOn) {
      return canAddPlayersFromRoster && hasBasicNonCanlanPermissionsResult
    }
    return hasBasicNonCanlanPermissionsResult
  }

  // For Canlan rosters

  // Salesforce sync restriction
  if (isSalesforceBlocking(sync_salesforce, adminPermission)) {
    return false
  }

  // Basic permission check
  return updatePermission && Boolean(rosterId)
}

/**
 * Determines if a user is allowed to add team officials based on their permissions and organization type.
 *
 * For Non-Canlan Organizations:
 * - Admin can always add officials
 * - Non-admin needs update permission, valid roster ID and organization
 * - Lock dates do not affect team official additions
 *
 * For Canlan Organizations:
 * - Admin can always add officials
 * - Non-admin needs update permission, valid roster ID and organization
 * - Salesforce sync must be disabled for non-admin users
 *
 * @param {ShouldAllowAddTeamOfficialsParams} params - Permission check parameters
 * @param {boolean} params.adminPermission - Whether user has admin permission
 * @param {boolean} params.updatePermission - Whether user has update permission
 * @param {string} params.rosterId - The roster ID
 * @param {boolean} params.sync_salesforce - Whether Salesforce sync is enabled
 * @param {Organization} params.organization - The organization object
 * @returns {boolean} Whether user is allowed to add team officials
 */
export const shouldAllowAddTeamOfficials = ({
  adminPermission,
  updatePermission,
  rosterId,
  sync_salesforce,
  organization,
}: ShouldAllowAddTeamOfficialsParams) => {
  // For non-Canlan rosters
  if (!isCanlan) {
    // No special lock date handling for team officials, so we pass false
    return hasBasicNonCanlanPermissions({
      adminPermission,
      updatePermission,
      rosterId,
      organization,
      isCurrentDateAfterDivisionOrCompetitionLockDate: false,
    })
  }
  // For Canlan rosters
  // Salesforce sync restriction
  if (isSalesforceBlocking(sync_salesforce, adminPermission)) {
    return false
  }
  // Basic permission check
  return updatePermission && Boolean(rosterId) && Boolean(organization?.id)
}
